import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faReply, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { BZ2_DELETE_REVIEW, BZ2_DELETE_REVIEW_REPLY, BZ2_REPLY_THE_REVIEW } from 'mk/autogenerated/translations/Review.14dc80f8ab4af772e75ce8269b5b4aa0'
import * as productUtils from 'mk/bazaar/product/utils';
import { photoUrl } from 'mk/photo/photoUrl';
import {
    bazaar_single_page_item_detail_url,
} from 'mk/urls/functions';
import ReviewReplyForm, { ReplyFormData } from 'mk2/apps/bazaar/components/ReviewReplyForm';
import { RECENT_REVIEW_DAYS } from 'mk2/apps/bazaar/constants';
import { REVIEW_REPLY_FORM } from 'mk2/apps/bazaar/helpers';
import { ReviewEntity, ReviewRating } from 'mk2/apps/bazaar/schemas';
import { bazaarMyBazaarUrl, bazaarSellerUrl } from 'mk2/apps/bazaar/urls';
import { AvatarImg } from 'mk2/components/AvatarImg';
import { Btn, BtnType } from 'mk2/components/Btn';
import { Emojify } from 'mk2/components/Emojify';
import { Img } from 'mk2/components/Img';
import { Link } from 'mk2/components/Link';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { timeAt } from 'mk2/helpers/timeformat';
import { url } from 'mk2/helpers/urls';
import useEventCallback from 'mk2/hooks/useEventCallback';
import useHiddenForm from 'mk2/hooks/useHiddenForm';
import React, { useCallback } from 'react';
import styles from './Review.mscss';

interface OwnProps {
    className?: string;
    isReviewer?: boolean;
    isReviewed?: boolean;
    isAdmin?: boolean;
    isLast?: boolean;
    review: ReviewEntity;

    deleting?: boolean;
    deletingReply?: boolean;

    onDelete(id: number);
    onDeleteReply(id: number);
    onReply(id: number, values: ReplyFormData);
}

type Props = OwnProps;

const ratingClassNames = {
    [ReviewRating.VERY_SATISFIED]: styles['Review__rating__icon--verySatisfied'],
    [ReviewRating.UNSATISFIED]: styles['Review__rating__icon--unsatisfied'],
};

const ratingIcons = {
    [ReviewRating.VERY_SATISFIED]: faThumbsUp,
    [ReviewRating.UNSATISFIED]: faThumbsDown,
};

const replyInitialValues = {
    message: '',
    photos: [],
};

const Review = React.memo<Props>(
    ({
        className,
        review,
        isReviewer = false,
        isReviewed = false,
        isAdmin = false,
        isLast = false,
        onDelete,
        onDeleteReply,
        onReply,
        deleting = false,
        deletingReply = false,
    }) => {
        const [showReplyForm, onReplyToggleClick, setShowReplyForm] = useHiddenForm(
            replyInitialValues,
        );

        const urlToReviewer = isReviewer
            ? url(bazaarMyBazaarUrl, {username: review.reviewer.username})
            : url(bazaarSellerUrl, {username: review.reviewer.username});

        const urlToReviewed = isReviewed
            ? url(bazaarMyBazaarUrl, {username: review.reviewed.username})
            : url(bazaarSellerUrl, {username: review.reviewed.username});

        // we show only ratings for reviews newer than 90 days
        const showRating =
            new Date(review.date) > new Date(new Date().getTime() - RECENT_REVIEW_DAYS);

        const productParams = review.product && productUtils.slugToParams(review.product.slug);

        const onDeleteClick = useEventCallback(
            (event: React.MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                onDelete(review.id);
            },
            [review.id, onDelete],
        );

        const onDeleteReplyClick = useEventCallback(
            (event: React.MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();
                onDeleteReply(review.id);
                setShowReplyForm(false);
            },
            [review.id, onDeleteReply, setShowReplyForm],
        );

        const onReplySubmit = useCallback(
            (values: ReplyFormData) => {
                onReply(review.id, values);
            },
            [review.id, onReply],
        );

        let action;
        if (isReviewed && !review.replyDate && !showReplyForm) {
            action = (
                <Btn
                    type={BtnType.Link}
                    label={BZ2_REPLY_THE_REVIEW}
                    onClick={onReplyToggleClick}
                    icon={faReply}
                />
            );
        } else if (isAdmin || isReviewer) {
            action = (
                <Btn
                    type={isReviewer ? BtnType.Link : BtnType.AdminTextLight}
                    label={BZ2_DELETE_REVIEW}
                    onClick={onDeleteClick}
                    icon={faTrash}
                    hasSpinner={deleting}
                />
            );
        }

        let actionReply;
        if (review.replyDate && (isAdmin || isReviewed)) {
            actionReply = (
                <Btn
                    type={isReviewed ? BtnType.Link : BtnType.AdminTextLight}
                    label={BZ2_DELETE_REVIEW_REPLY}
                    onClick={onDeleteReplyClick}
                    icon={faTrash}
                    hasSpinner={deletingReply}
                />
            );
        }

        return (
            <div className={cx(styles.Review, className)}>
                <div
                    className={cx(
                        styles.Review__message,
                        (!isLast || review.replyDate || showReplyForm) &&
                            styles['Review__message--notLast'],
                        review.isNew && (isReviewed || isAdmin) && styles['Review__message--new'],
                    )}
                >
                    <div className={styles.Review__user}>
                        <Link to={urlToReviewer} className={styles.Review__user__avatar}>
                            <AvatarImg size={32} user={review.reviewer} />
                        </Link>
                        <div className={styles.Review__user__info}>
                            <Link
                                to={urlToReviewer}
                                className={styles.Review__user__info__username}
                            >
                                {review.reviewer.username}
                            </Link>
                            <div className={styles.Review__user__info__date}>
                                {timeAt(review.date)}
                            </div>
                        </div>
                    </div>
                    <div className={styles.Review__rating}>
                        {showRating && (
                            <span
                                className={cx(
                                    styles.Review__rating__icon,
                                    ratingClassNames[review.rating],
                                )}
                            >
                                <FontAwesomeIcon icon={ratingIcons[review.rating]} size="2x" />
                            </span>
                        )}
                    </div>
                    <div className={styles.Review__content}>
                        {review.photo && (
                            <a
                                data-google-interstitial="false"
                                href={photoUrl(review.photo, 's1600x1600')}
                                target="_blank"
                                className={styles.Review__photo}
                            >
                                <Img
                                    width={40}
                                    src={photoUrl(review.photo, 'c80x80')}
                                    height={40}
                                    className={styles.Review__photo__image}
                                />
                            </a>
                        )}
                        <div className={styles.Review__content__text}>
                            <Emojify>{review.message}</Emojify>{' '}
                            {isAdmin && review.product && (
                                <Link
                                    className={styles.Review__productLink}
                                    to={bazaar_single_page_item_detail_url(
                                        productParams.category_slug,
                                        productParams.hash,
                                        productParams.title,
                                    )}
                                >
                                    ({review.product.title.trim()})
                                </Link>
                            )}
                        </div>
                    </div>
                    {action && <div className={styles.Review__action}>{action}</div>}
                </div>

                {review.replyDate && (
                    <div
                        className={cx(
                            styles.Review__reply,
                            !isLast && styles['Review__reply--notLast'],
                        )}
                    >
                        <div className={cx(styles.Review__user, styles.Review__reply__user)}>
                            <Link to={urlToReviewed} className={styles.Review__user__avatar}>
                                <AvatarImg size={32} user={review.reviewed} />
                            </Link>
                            <div className={styles.Review__user__info}>
                                <Link
                                    to={urlToReviewed}
                                    className={styles.Review__user__info__username}
                                >
                                    {review.reviewed.username}
                                </Link>
                                <div className={styles.Review__user__info__date}>
                                    {timeAt(review.replyDate)}
                                </div>
                            </div>
                        </div>

                        <div className={cx(styles.Review__content, styles.Review__reply__content)}>
                            {review.replyPhoto && (
                                <a
                                    data-google-interstitial="false"
                                    href={photoUrl(review.replyPhoto, 's1600x1600')}
                                    target="_blank"
                                    className={styles.Review__photo}
                                >
                                    <Img
                                        width={40}
                                        src={photoUrl(review.replyPhoto, 'c80x80')}
                                        height={40}
                                        className={styles.Review__photo__image}
                                    />
                                </a>
                            )}
                            <div className={styles.Review__content__text}>
                                <Emojify>{review.replyMessage}</Emojify>
                            </div>
                        </div>

                        {actionReply && <div className={styles.Review__action}>{actionReply}</div>}
                    </div>
                )}

                {!review.replyDate && isReviewed && showReplyForm && (
                    <div
                        className={cx(
                            styles.Review__replyForm,
                            !isLast && styles['Review__replyForm--notLast'],
                        )}
                    >
                        <ReviewReplyForm
                            form={REVIEW_REPLY_FORM(review.id)}
                            onSave={onReplySubmit}
                            onCancel={onReplyToggleClick}
                            initialValues={replyInitialValues}
                        />
                    </div>
                )}
            </div>
        );
    },
);

Review.displayName = 'Review';

export default Review;

interface PlaceholderOwnProps {
    className?: string;
    isLast?: boolean;
    textWidth?: number;
}

export const ReviewPlaceholder = React.memo<PlaceholderOwnProps>(
    ({ className, isLast = false, textWidth = 200 }) => (
        <div className={cx(styles.Review, className)}>
            <div
                className={cx(
                    styles.Review__message,
                    !isLast && styles['Review__message--notLast'],
                )}
            >
                <div className={styles.Review__user}>
                    <span className={styles.Review__user__avatar}>
                        <LoadingPlaceholder width={32} height={32} />
                    </span>
                    <div className={styles.Review__user__info}>
                        <span className={styles.Review__user__info__username}>
                            <LoadingPlaceholder width={80} />
                        </span>
                        <div className={styles.Review__user__info__date}>
                            <LoadingPlaceholder width={57} />
                        </div>
                    </div>
                </div>
                <div className={styles.Review__rating} />
                <div className={styles.Review__content}>
                    <div className={styles.Review__content__text}>
                        <LoadingPlaceholder width={textWidth} />
                    </div>
                </div>
            </div>
        </div>
    ),
);
